import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang, LOCALES } from "@/lib/i18n";
import StatusBadge from "@/components/StatusBadge";

const TZ = "Asia/Gaza";
const fullFmt = (lang) => new Intl.DateTimeFormat(LOCALES[lang] || "ar-EG-u-nu-latn", { timeZone: TZ, weekday: "long", day: "numeric", month: "long", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true });

export function relativeAgo(ms, lang) {
    const m = Math.max(0, Math.round(ms / 60000));
    const h = Math.floor(m / 60), d = Math.floor(h / 24);
    if (lang !== "ar") {
        try {
            const rtf = new Intl.RelativeTimeFormat(LOCALES[lang] || "en-GB", { numeric: "auto" });
            if (m < 1) return rtf.format(0, "minute").replace(/^in /, "");
            if (m < 60) return rtf.format(-m, "minute");
            if (h < 24) return rtf.format(-h, "hour");
            return rtf.format(-d, "day");
        } catch { return `${m}m`; }
    }
    if (m < 1) return "قبل لحظات";
    if (m < 60) return m === 1 ? "منذ دقيقة" : m === 2 ? "منذ دقيقتين" : m <= 10 ? `منذ ${m} دقائق` : `منذ ${m} دقيقة`;
    if (h < 24) return h === 1 ? "منذ ساعة" : h === 2 ? "منذ ساعتين" : h <= 10 ? `منذ ${h} ساعات` : `منذ ${h} ساعة`;
    return d === 1 ? "منذ يوم" : d === 2 ? "منذ يومين" : d <= 10 ? `منذ ${d} أيام` : `منذ ${d} يومًا`;
}

/** «آخر تحديث للأخبار» — server-sourced editorial time for the shown language. Hidden when no trustworthy time.
 *  Re-fetched with the news feed (articles:* events / home load), plus a light minute tick for the wording. */
export default function NewsUpdatedBadge() {
    const { lang, t } = useLang();
    const [data, setData] = useState(null);      // {updated_at, server_now, received}
    const [, force] = useState(0);
    const [open, setOpen] = useState(false);
    useEffect(() => {
        let alive = true;
        const load = () => api.get("/public/news-updated", { params: { lang } }).then((r) => { if (alive) setData({ ...r.data, received: Date.now() }); }).catch(() => {});
        load();
        const evs = ["articles:applied", "articles:update", "articles:removed", "home:loaded"];
        evs.forEach((e) => window.addEventListener(e, load));
        const tick = setInterval(() => force((x) => x + 1), 60000);
        const onVis = () => { if (document.visibilityState === "visible") load(); };
        document.addEventListener("visibilitychange", onVis); window.addEventListener("online", load);
        return () => { alive = false; evs.forEach((e) => window.removeEventListener(e, load)); clearInterval(tick); document.removeEventListener("visibilitychange", onVis); window.removeEventListener("online", load); };
    }, [lang]);
    if (!data?.updated_at) return null;
    // Elapsed = server clock at fetch + time passed locally since (client clock skew never matters)
    const elapsed = (new Date(data.server_now) - new Date(data.updated_at)) + (Date.now() - data.received);
    const rel = relativeAgo(elapsed, lang);
    const full = fullFmt(lang).format(new Date(data.updated_at));
    return (
        <span className="relative inline-flex">
            <StatusBadge as="button" type="button" testId="news-updated-badge" onClick={() => setOpen((o) => !o)} onBlur={() => setOpen(false)} onKeyDown={(e) => e.key === "Escape" && setOpen(false)} aria-expanded={open} aria-describedby={open ? "news-updated-full" : undefined} title={full}>
                {t("newsUpdated")}: <time dateTime={data.updated_at} data-testid="news-updated-rel">{rel}</time>
            </StatusBadge>
            {open && (
                <span id="news-updated-full" role="tooltip" className="news-updated-pop" data-testid="news-updated-full">{full} ({t("gazaTimeLabel")})</span>
            )}
        </span>
    );
}
